import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const groupApi = createApi({
  reducerPath: 'groupApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v1/' }),
  tagTypes: ['Group', 'GroupUsers', 'GroupPermissions'],
  endpoints: (builder) => ({
    getGroups: builder.query({
      query: ({ page, per_page }) => `/groups?page=${page}&per_page=${per_page}`,
      providesTags: ['Group']
    }),    
    getGroup: builder.query({
      query: (id) => `groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'Group', id }],
    }),
    createGroup: builder.mutation({
      query: (group) => ({
        url: 'groups',
        method: 'POST',
        body: group,
      }),
      invalidatesTags: ['Group'],
    }),
    updateGroup: builder.mutation({
      query: ({ id, ...group }) => ({
        url: `groups/${id}`,
        method: 'PUT',
        body: group,
      }),
      invalidatesTags: ['Group'],
    }),
    deleteGroup: builder.mutation({
      query: (id) => ({
        url: `groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Group', id },
        'Group',
        { type: 'GroupUsers', id },
        { type: 'GroupPermissions', id }
      ],
    }),
    getGroupUsers: builder.mutation({
      query: ({ groupId, search, assigned }) => ({
        url: `groups/${groupId}/users_in_group`,
        method: 'POST',
        body: { search, assigned },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupUsers', id: groupId }
      ]
    }),
    
    addUserToGroup: builder.mutation({
      query: ({ groupId, userId }) => ({
        url: `groups/${groupId}/add_user`,
        method: 'POST',
        body: { user_id: userId },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'Group', id: groupId }],
    }),
    removeUserFromGroup: builder.mutation({
      query: ({ groupId, userId }) => ({
        url: `groups/${groupId}/remove_user`,
        method: 'DELETE',
        body: { user_id: userId },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'Group', id: groupId }],
    }),
    getGroupPermissions: builder.query({
      query: ({ groupId, permissible_type }) => 
        `groups/${groupId}/permissions?permissible_type=${permissible_type}`,
      providesTags: (result, error, { groupId }) => [
        { type: 'GroupPermissions', id: groupId }
      ]
    }),
    addPermission: builder.mutation({
      query: ({ groupId, ...body }) => ({
        url: `groups/${groupId}/add_permission`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'Group', id: groupId },
        { type: 'GroupPermissions', id: groupId }
      ]
    }),
    removePermission: builder.mutation({
      query: ({ groupId, ...body }) => ({
        url: `groups/${groupId}/remove_permission`,
        method: 'DELETE',
        body
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'Group', id: groupId },
        { type: 'GroupPermissions', id: groupId }
      ]
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupUsersMutation,
  useAddUserToGroupMutation,
  useRemoveUserFromGroupMutation,
  getGroupPermissions,
  useAddPermissionMutation,
  useRemovePermissionMutation 
} = groupApi;